import { Category } from '@Client/runner.directives/common/Category/Category';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import { RunnerPageDescription } from '@Client/runner.directives/common/PageDescription';
import { getService } from '@Client/runner.hooks/useService';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';
import { IFormattedCategory } from '@Client/runner.services/start.flows.formatter';
import FlowModelSummary, {
  IFlowModelSummary
} from '@Client/runner.shared/FlowModelSummary/FlowModelSummary';
import { FlowModelPublishType } from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelPublishType';
import IPublishedFlowModel from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/publishedFlowModel';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import {
  IFlowModelDeletedMessage,
  IFlowModelPublishedMessage,
  PubSubEvent
} from '@Shared.Angular/@types/pubSub';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import {
  CustomTabs,
  ICustomTab
} from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import Search from '@Shared.Angular/flowingly.components/Search/Search';
import { TagFilter } from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import { naturalSortComparator } from '@Shared.Angular/flowingly.helper/naturalSort';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import React, { useCallback, useEffect, useState } from 'react';

type Props = {
  backFromState?: string;
};

const FlowsActive = (props: Props) => {
  const { backFromState } = props;
  const APP_CONFIG = getService('APP_CONFIG');
  const permissionsService = getService('permissionsService');
  const sessionService = getService('sessionService');
  const $state = getService('$state');
  const flowListManager = getService('flowListManager');
  const runnerCategoryNavigationService = getService(
    'runnerCategoryNavigationService'
  );
  const appInsightsService = getService('appInsightsService');
  const pubsubService = getService('pubsubService');
  const brandingService = getService('brandingService');

  if (
    !permissionsService.currentUserHasPermission(
      FlowinglyPermissions.FLOW_START
    )
  ) {
    $state.go(STATE_MAPPINGS.flowsin);
    return;
  }

  const allCategoriesId = 'all-categories';

  const [categories, setCategories] = useState<
    ITreeCategory<IPublishedFlowModel>[]
  >([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
    backFromState === STATE_MAPPINGS.flow
      ? flowListManager.getActiveFlowCategoryId()
      : allCategoriesId
  );
  const [displayedFlowModels, setDisplayedFlowModels] = useState<
    IPublishedFlowModel[]
  >([]);
  const [dataIsReady, setDataIsReady] = useState(false);
  const [lowerCaseSearchText, setLowerCaseSearchText] = useState('');
  const [tagFilterIds, setTagFilterIds] = useState<Guid[]>([]);
  const [allFlowModelTagIds, setAllFlowModelTagIds] = useState<Guid[]>([]);
  const [selectedTabId, setSelectedTabId] = useState<FlowsActiveTabId>(
    FlowsActiveTabId.ALL
  );

  const canCreateFlowModels = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.FLOWMODEL_CREATE
  );
  const isDefaultBranding = brandingService.usingDefaultBrandName();

  const [tabs] = useState<ICustomTab[]>([
    { id: FlowsActiveTabId.ALL, label: 'All workflows' },
    { id: FlowsActiveTabId.FREQUENT, label: 'Frequently started' }
  ]);

  const getEmptyStateBodyText = (isFiltered: boolean) => {
    if (isFiltered && selectedTabId === FlowsActiveTabId.FREQUENT) {
      return 'You have not started any workflows matching the current filters.';
    }
    return canCreateFlowModels
      ? `Use ${APP_CONFIG.brandingName} to easily map and automate your repetitive business processes.<br /> To start a Flow, you must have at least one Workflow published to you.`
      : 'To start a Flow, you must have at least one Workflow published to you.<br />Contact your administrator to have Workflows shared with you.';
  };

  const getEmptyStateButtonText = (isFiltered: boolean) => {
    if (isFiltered && selectedTabId === FlowsActiveTabId.FREQUENT) {
      return '';
    }
    return canCreateFlowModels ? 'Create and Publish a Workflow' : '';
  };

  const refreshFlows = () => {
    return flowListManager
      .getCategorizedWorkflows()
      .then((workflowsByCategory) => {
        addAllFlowsCategory(workflowsByCategory);
        const treeViewCategories =
          runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
            workflowsByCategory
          );
        setCategories(treeViewCategories);
      });
  };

  const addAllFlowsCategory = (
    categories: IFormattedCategory<IPublishedFlowModel>[]
  ) => {
    if (
      !categories ||
      categories.find((c) => (c.id as string) === allCategoriesId)
    ) {
      return;
    }
    const copyOfAllFlows =
      runnerCategoryNavigationService.getFlowModelsFromNestedCategories<IPublishedFlowModel>(
        runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
          categories
        )
      );
    categories.unshift({
      name: 'All',
      id: allCategoriesId,
      flowModels: copyOfAllFlows,
      subCategories: []
    });
  };

  const selectCategory = (categoryId: string) => {
    const targetCategory =
      runnerCategoryNavigationService.findNodeById<IPublishedFlowModel>(
        categories,
        categoryId
      );
    if (!targetCategory) {
      return;
    }
    if (categoryId != null) {
      flowListManager.setActiveFlowCategoryId(categoryId);
    }

    setSelectedCategoryId(categoryId);
  };

  const updateDisplayFlowModels = () => {
    const selectedCategory =
      runnerCategoryNavigationService.findNodeById<IPublishedFlowModel>(
        categories,
        selectedCategoryId
      );
    if (!selectedCategory?.flowModels) {
      setDisplayedFlowModels([]);
      return;
    }
    const flowModels = selectedCategory.flowModels.filter((flowModel) => {
      const matchesSearchText =
        !lowerCaseSearchText ||
        flowModel.name.toLowerCase().includes(lowerCaseSearchText) ||
        (flowModel.description &&
          flowModel.description.toLowerCase().includes(lowerCaseSearchText));
      const matchesTagFilter =
        tagFilterIds.length === 0 ||
        (flowModel.parsedTags &&
          flowModel.parsedTags.some((tag) => tagFilterIds.includes(tag.id)));
      return matchesSearchText && matchesTagFilter;
    });
    if (selectedTabId === FlowsActiveTabId.ALL) {
      const orderedFlowModels = flowModels.toSorted(
        (leftFlowModel, rightFlowModel) =>
          naturalSortComparator(leftFlowModel.name, rightFlowModel.name)
      );

      setDisplayedFlowModels(orderedFlowModels);
    } else if (selectedTabId === FlowsActiveTabId.FREQUENT) {
      const orderedFlowModels = flowModels.toSorted(
        (leftFlowModel, rightFlowModel) => {
          const rightCount = rightFlowModel.countOfFlowStarted ?? 0;
          const leftCount = leftFlowModel.countOfFlowStarted ?? 0;
          const countDifference = rightCount - leftCount;

          if (countDifference !== 0) {
            return countDifference;
          }

          return naturalSortComparator(leftFlowModel.name, rightFlowModel.name);
        }
      );
      setDisplayedFlowModels(orderedFlowModels);
    }
  };

  const updateTagFilter = (tagIds: Guid[]) => {
    setTagFilterIds(tagIds);
  };

  const goToFlowStartPage = (flowModelId: Guid) => {
    $state.go(
      STATE_MAPPINGS.flow,
      { flowId: EmptyGuid, flowModelId: flowModelId },
      { notify: true }
    );
  };

  const onFlowModelNameChanged = useCallback(
    (msg: PubSubEvent, data: string) => {
      const parsedData = JSON.parse(data) as { id: Guid; name: string };
      const flowUpdated = categories.some((category) =>
        category.flowModels.some((flowModel) => {
          if (flowModel.id === parsedData.id) {
            flowModel.name = parsedData.name;
            return true;
          }
          return false;
        })
      );
      if (flowUpdated) {
        setCategories([...categories]);
      }
    },
    [categories]
  );

  const removeFlowModel = useCallback(
    (flowModelId: Guid) => {
      const removed = runnerCategoryNavigationService.removeItem(
        categories,
        flowModelId
      );
      if (removed) {
        setCategories([...categories]);
      }
    },
    [categories]
  );

  const addFlowModel = useCallback(
    (publishedFlowModel: IPublishedFlowModel) => {
      setCategories((prevCategories) => {
        runnerCategoryNavigationService.addItem(
          prevCategories,
          allCategoriesId,
          publishedFlowModel
        );
        return [...prevCategories];
      });
    },
    [allCategoriesId]
  );

  const removeActorFromFlowModels = useCallback(
    (actorName: string) => {
      let updatedFlowModels = false;
      categories.forEach((category) => {
        category.flowModels.forEach((flowModel) => {
          if (flowModel.processOwnerName === actorName) {
            flowModel.processOwnerName = null;
            updatedFlowModels = true;
          }
        });
      });

      if (updatedFlowModels) {
        setCategories([...categories]);
      }
    },
    [categories]
  );

  useEffect(() => {
    refreshFlows().then(() => {
      appInsightsService.trackMetricIfTimerExist('flowsActiveEntered');
    });
  }, []);

  useEffect(() => {
    const activeFlowsSubscriberId = 'flowingly.runner.flows.active';
    pubsubService.subscribe(
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      onFlowModelNameChanged,
      activeFlowsSubscriberId
    );
    pubsubService.subscribe(
      'SIGNALR_SETUP_CATEGORY_DELETED',
      refreshFlows,
      activeFlowsSubscriberId
    );
    pubsubService.subscribe<IFlowModelDeletedMessage>(
      'SIGNALR_SETUP_FLOW_MODEL_DELETED',
      (eventName, message) => {
        removeFlowModel(message.flowModelId);
      },
      activeFlowsSubscriberId
    );
    pubsubService.subscribe(
      'SIGNALR_WORKFLOW_PUBLISHED',
      (event, message: IFlowModelPublishedMessage) => {
        const user = sessionService.getUser();
        const userCanSeeFlowModel =
          message.publishType === FlowModelPublishType.Workflow &&
          (message.isPublishedToEveryone ||
            message.publishedToUserIds.includes(user.id) ||
            message.publishedToTeamIds.some((teamId) =>
              user.teams.some((userTeam) => userTeam.id === teamId)
            ));
        removeFlowModel(message.flowModelId);
        if (userCanSeeFlowModel) {
          addFlowModel(message.published);
        }
      },
      activeFlowsSubscriberId
    );
    pubsubService.subscribe(
      'SIGNALR_WORKFLOW_UNPUBLISHED',
      refreshFlows,
      activeFlowsSubscriberId
    );
    pubsubService.subscribe(
      'SIGNALR_ACTOR_DELETED',
      (event, jsonParams: string) => {
        const params = JSON.parse(jsonParams);
        removeActorFromFlowModels(params.actorName);
      },
      activeFlowsSubscriberId
    );

    return () => {
      pubsubService.unsubscribeAll(activeFlowsSubscriberId);
    };
  }, [
    removeFlowModel,
    addFlowModel,
    onFlowModelNameChanged,
    removeActorFromFlowModels
  ]);

  useEffect(() => {
    if (backFromState === STATE_MAPPINGS.flow) {
      const activeCategoryId = flowListManager.getActiveFlowCategoryId();
      selectCategory(activeCategoryId);
    } else {
      selectCategory(selectedCategoryId);
    }
    const usedTagids = categories.flatMap((c) =>
      c.flowModels.flatMap(
        (flowModel) => flowModel.parsedTags?.map((tag) => tag.id) || []
      )
    );
    setAllFlowModelTagIds(usedTagids);
  }, [categories]);

  useEffect(() => {
    updateDisplayFlowModels();
    if (categories.length > 0) {
      setDataIsReady(true);
    }
  }, [
    categories,
    selectedTabId,
    selectedCategoryId,
    lowerCaseSearchText,
    tagFilterIds
  ]);

  const hasPublishedFlowModels: boolean = categories.some(
    (category) => category.id !== allCategoriesId
  );

  return (
    <div id="flows-active" className="runner-page">
      <div className="mobile-hide">
        <RunnerPageDescription
          pageHeaderText="Start Flows"
          pageDescription="A list of workflows published to you."
          helpLink="https://help.flowingly.net/getting-started/how-do-i-start-a-flow"
        />
      </div>
      {dataIsReady && !hasPublishedFlowModels && (
        <div className="mobile-hide">
          <EmptyState
            summaryText={
              canCreateFlowModels ? 'You have no Workflows' : 'Start Flows'
            }
            bodyText={getEmptyStateBodyText(false)}
            buttonText={getEmptyStateButtonText(false)}
            onButtonClick={
              canCreateFlowModels
                ? () => $state.go(STATE_MAPPINGS.librarydrafts)
                : undefined
            }
            demoUrl={isDefaultBranding ? APP_CONFIG.workflowDemoUrl : undefined}
          />
        </div>
      )}
      {categories.length > 0 && (
        <div
          className={`flows-active-content runner-page__content ${
            hasPublishedFlowModels ? '' : 'desktop-hide'
          }`}
        >
          {hasPublishedFlowModels && (
            <Category
              categorisedItems={categories}
              selectedCategoryId={selectedCategoryId}
              onCategoryClick={selectCategory}
              defaultItemId={allCategoriesId}
            />
          )}
          <div className="flows-active-list runner-page__content__main-wrapper">
            <div className="runner-page__content__main-content">
              <div className="toolbar">
                <Search
                  value={lowerCaseSearchText}
                  onValueChange={(newText: string) => {
                    setLowerCaseSearchText(newText.toLowerCase());
                  }}
                  placeholder="Search by workflow name or description"
                />
                <div className="filter-container mobile-hide">
                  <CustomTabs
                    tabItems={tabs}
                    selectedTabId={selectedTabId}
                    onChange={(newTabId) =>
                      setSelectedTabId(newTabId as FlowsActiveTabId)
                    }
                  />
                  <TagFilter
                    usedTagIds={allFlowModelTagIds}
                    onApply={updateTagFilter}
                  ></TagFilter>
                </div>
              </div>
              {displayedFlowModels.map((flowModel) => {
                const summary: IFlowModelSummary = {
                  id: flowModel.id,
                  name: flowModel.name,
                  description: flowModel.description,
                  category: flowModel.category,
                  processOwnerName: flowModel.processOwnerName || 'N/A',
                  parsedTags: flowModel.parsedTags || [],
                  isConfidential: flowModel.isConfidential || false
                };
                return (
                  <FlowModelSummary
                    key={summary.id}
                    flowModelSummary={summary}
                    showConfidentialType={true}
                    onFlowModelClick={goToFlowStartPage}
                  />
                );
              })}
              {displayedFlowModels.length === 0 && hasPublishedFlowModels && (
                <div className="mobile-hide">
                  {(() => {
                    const isFiltered =
                      lowerCaseSearchText !== '' ||
                      tagFilterIds.length > 0 ||
                      selectedCategoryId !== allCategoriesId;

                    const isSearchEmptyState =
                      hasPublishedFlowModels && isFiltered;

                    const summaryText = isSearchEmptyState
                      ? 'No matching Flows found'
                      : 'Hi ' + sessionService.getUser().fullName.split(' ')[0];
                    const bodyText = isSearchEmptyState
                      ? 'Try adjusting your search or filter criteria.'
                      : getEmptyStateBodyText(isFiltered);
                    const buttonText = isSearchEmptyState
                      ? ''
                      : getEmptyStateButtonText(isFiltered);

                    return (
                      <EmptyState
                        summaryText={summaryText}
                        bodyText={bodyText}
                        buttonText={buttonText}
                        onButtonClick={
                          buttonText
                            ? () => $state.go(STATE_MAPPINGS.librarydrafts)
                            : undefined
                        }
                      />
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowsActive;

const enum FlowsActiveTabId {
  ALL = 'all',
  FREQUENT = 'frequent'
}
